'use client';

import { useState, useEffect } from 'react';
import '@/styles/brutalist/contact-modal.css';

interface ContactForwarderModalProps {
  open: boolean;
  onClose: () => void;
  forwarderId: string;
  forwarderName: string;
  sourcePage: string;
}

interface FormData {
  name: string;
  email: string;
  company: string;
  message: string;
  origin: string;
  destination: string;
  cargoType: string;
  readyDate: string;
}

export function ContactForwarderModal({
  open,
  onClose,
  forwarderId,
  forwarderName,
  sourcePage,
}: ContactForwarderModalProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    message: '',
    origin: '',
    destination: '',
    cargoType: '',
    readyDate: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<'success' | 'error' | null>(null);
  const [showShipmentDetails, setShowShipmentDetails] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitResult(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          forwarder_id: forwarderId,
          sender_name: formData.name,
          sender_email: formData.email,
          sender_company: formData.company || null,
          message: formData.message,
          shipment_details: showShipmentDetails ? {
            origin: formData.origin || null,
            destination: formData.destination || null,
            cargo_type: formData.cargoType || null,
            ready_date: formData.readyDate || null,
          } : null,
          source_page: sourcePage,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setSubmitResult('success');
      // Reset form after success
      setFormData({
        name: '',
        email: '',
        company: '',
        message: '',
        origin: '',
        destination: '',
        cargoType: '',
        readyDate: '',
      });
      setShowShipmentDetails(false);
    } catch (error) {
      console.error('Contact error:', error);
      setSubmitResult('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitResult(null);
    onClose();
  };

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    
    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [open, onClose]);

  if (!open) return null;

  // Success state
  if (submitResult === 'success') {
    return (
      <div className="brutalist-modal-overlay" onClick={handleClose}>
        <div className="brutalist-modal" onClick={(e) => e.stopPropagation()}>
          <div className="brutalist-success-container">
            <div className="brutalist-success-icon-wrapper">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h3 className="brutalist-success-title">Message Sent!</h3>
            <p className="brutalist-success-text">
              Your message has been sent to {forwarderName}. They will respond directly to your email.
            </p>
            <button className="brutalist-btn brutalist-btn-primary" onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="brutalist-modal-overlay" onClick={handleClose}>
      <div className="brutalist-modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="brutalist-modal-header">
            <div className="brutalist-modal-title-wrapper">
              <h2 className="brutalist-modal-title">
                <svg className="brutalist-modal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="4" width="20" height="16" />
                  <path d="M2 6l10 7 10-7" />
                </svg>
                Contact {forwarderName}
              </h2>
              <p className="brutalist-modal-description">
                Send a message to this freight forwarder. They will respond directly to your email.
              </p>
            </div>
            <button
              type="button"
              className="brutalist-modal-close"
              onClick={handleClose}
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="brutalist-modal-body">
            {submitResult === 'error' && (
              <div className="brutalist-alert brutalist-alert-error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                Failed to send message. Please try again.
              </div>
            )}

            {/* Contact Info */}
            <div className="brutalist-form-grid">
              <div className="brutalist-form-group">
                <label htmlFor="name" className="brutalist-form-label brutalist-form-label-required">
                  Your Name
                </label>
                <input
                  id="name"
                  type="text"
                  className="brutalist-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="John Smith"
                />
              </div>
              <div className="brutalist-form-group">
                <label htmlFor="email" className="brutalist-form-label brutalist-form-label-required">
                  Your Email
                </label>
                <input
                  id="email"
                  type="email"
                  className="brutalist-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="john@company.com"
                />
              </div>
            </div>

            <div className="brutalist-form-group">
              <label htmlFor="company" className="brutalist-form-label">
                Company Name
              </label>
              <div className="brutalist-input-icon-wrapper">
                <svg className="brutalist-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                </svg>
                <input
                  id="company"
                  type="text"
                  className="brutalist-input"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Your company name"
                />
              </div>
            </div>

            <div className="brutalist-form-group">
              <label htmlFor="message" className="brutalist-form-label brutalist-form-label-required">
                Message
              </label>
              <textarea
                id="message"
                className="brutalist-textarea"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                placeholder="Describe your shipment needs..."
                rows={4}
              />
            </div>

            {/* Shipment Details Toggle */}
            <div className="brutalist-form-group">
              <button
                type="button"
                onClick={() => setShowShipmentDetails(!showShipmentDetails)}
                className="brutalist-toggle-button"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" />
                  <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
                </svg>
                {showShipmentDetails ? 'Hide' : 'Add'} shipment details (optional)
              </button>
            </div>

            {/* Shipment Details */}
            {showShipmentDetails && (
              <div className="brutalist-shipment-details">
                <div className="brutalist-form-grid">
                  <div className="brutalist-form-group">
                    <label htmlFor="origin" className="brutalist-form-label">
                      Origin
                    </label>
                    <div className="brutalist-input-icon-wrapper">
                      <svg className="brutalist-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      <input
                        id="origin"
                        type="text"
                        className="brutalist-input"
                        value={formData.origin}
                        onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
                        placeholder="City, Country"
                      />
                    </div>
                  </div>
                  <div className="brutalist-form-group">
                    <label htmlFor="destination" className="brutalist-form-label">
                      Destination
                    </label>
                    <div className="brutalist-input-icon-wrapper">
                      <svg className="brutalist-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      <input
                        id="destination"
                        type="text"
                        className="brutalist-input"
                        value={formData.destination}
                        onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                        placeholder="City, Country"
                      />
                    </div>
                  </div>
                </div>

                <div className="brutalist-form-grid">
                  <div className="brutalist-form-group">
                    <label htmlFor="cargoType" className="brutalist-form-label">
                      Cargo Type
                    </label>
                    <select
                      id="cargoType"
                      className="brutalist-select"
                      value={formData.cargoType}
                      onChange={(e) => setFormData({ ...formData, cargoType: e.target.value })}
                    >
                      <option value="">Select type</option>
                      <option value="general">General Cargo</option>
                      <option value="fcl">Full Container (FCL)</option>
                      <option value="lcl">Less than Container (LCL)</option>
                      <option value="air">Air Freight</option>
                      <option value="hazardous">Hazardous/DG</option>
                      <option value="temperature">Temperature Controlled</option>
                      <option value="oversized">Oversized/Project</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="brutalist-form-group">
                    <label htmlFor="readyDate" className="brutalist-form-label">
                      Ready Date
                    </label>
                    <div className="brutalist-input-icon-wrapper">
                      <svg className="brutalist-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <input
                        id="readyDate"
                        type="date"
                        className="brutalist-input"
                        value={formData.readyDate}
                        onChange={(e) => setFormData({ ...formData, readyDate: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="brutalist-modal-footer">
            <button
              type="button"
              className="brutalist-btn brutalist-btn-outline"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="brutalist-btn brutalist-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  Sending...
                </>
              ) : (
                'Send Message'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
